import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ApartmentOutlined } from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { operatorName } from './liveChatMeta';

/**
 * بخش‌های پاسخ‌گویی (مثلاً پشتیبانی، فروش). مشتری در شروع گفتگو با بازوی بله یکی را انتخاب می‌کند.
 * اپراتور بدون بخش = پاسخ‌گوی همه بخش‌ها.
 */
const DepartmentsCard = () => {
  const [departments, setDepartments] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deps, ops] = await Promise.all([liveChatApi.getDepartments(), liveChatApi.getOperators()]);
      setDepartments(deps?.data ?? []);
      setOperators(ops?.data ?? []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت بخش‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (dep = null) => {
    setModal({ open: true, editing: dep });
    form.resetFields();
    form.setFieldsValue(
      dep
        ? {
            name: dep.name,
            description: dep.description,
            selected_message: dep.selected_message,
            is_active: dep.is_active,
            sort_order: dep.sort_order,
            operator_ids: (dep.operators || []).map((o) => o.id),
          }
        : { is_active: true, operator_ids: [] },
    );
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (modal.editing) await liveChatApi.updateDepartment(modal.editing.id, values);
      else await liveChatApi.createDepartment(values);
      message.success('بخش ذخیره شد');
      setModal({ open: false, editing: null });
      load();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره بخش');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (dep, isActive) => {
    try {
      await liveChatApi.updateDepartment(dep.id, { is_active: isActive });
      setDepartments((prev) => prev.map((d) => (d.id === dep.id ? { ...d, is_active: isActive } : d)));
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    }
  };

  const remove = async (dep) => {
    try {
      await liveChatApi.removeDepartment(dep.id);
      setDepartments((prev) => prev.filter((d) => d.id !== dep.id));
      message.success('بخش حذف شد');
    } catch (error) {
      message.error(error?.message || 'خطا در حذف بخش');
    }
  };

  const columns = [
    {
      title: 'بخش',
      key: 'name',
      render: (_, d) => (
        <div>
          <div className="font-medium custom-font">{d.name}</div>
          {d.description && <div className="text-xs text-gray-500 custom-font">{d.description}</div>}
        </div>
      ),
    },
    {
      title: 'اپراتورها',
      key: 'operators',
      render: (_, d) =>
        d.operators?.length ? (
          <Space size={[4, 4]} wrap>
            {d.operators.map((o) => (
              <Tag key={o.id} className="custom-font">{operatorName(o)}</Tag>
            ))}
          </Space>
        ) : (
          <Tooltip title="اپراتورهایی که به هیچ بخشی محدود نشده‌اند پاسخ‌گوی همه بخش‌ها هستند">
            <span className="text-xs text-gray-400 custom-font">همه اپراتورهای بدون بخش</span>
          </Tooltip>
        ),
    },
    { title: 'ترتیب', dataIndex: 'sort_order', key: 'sort_order', width: 70 },
    {
      title: 'فعال',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 70,
      render: (active, d) => <Switch size="small" checked={active} onChange={(v) => toggle(d, v)} />,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, d) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openModal(d)} />
          <Popconfirm title="این بخش حذف شود؟" description="گفتگوهای آن «بدون بخش» می‌شوند." okText="بله" cancelText="خیر" onConfirm={() => remove(d)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="livechat-panel p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="font-bold text-gray-800 dark:text-gray-100 custom-font flex items-center gap-2">
            <ApartmentOutlined className="text-brand-primary" /> بخش‌های پاسخ‌گویی
          </div>
          <div className="text-xs text-gray-500 custom-font mt-1">
            مشتری در شروع گفتگو با بازوی بله یکی از این بخش‌ها را انتخاب می‌کند و گفتگو فقط به اپراتورهای همان بخش نمایش داده می‌شود.
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className={PANEL_PRIMARY_BTN_CLASS} style={{ fontFamily: 'CustomFont' }}>
          افزودن بخش
        </Button>
      </div>
      <Spin spinning={loading}>
        {departments.length === 0 && !loading ? (
          <Empty className="py-6" description={<span className="custom-font">هنوز بخشی تعریف نشده (مثلاً «پشتیبانی» و «فروش»)</span>} />
        ) : (
          <Table rowKey="id" size="small" columns={columns} dataSource={departments} pagination={false} />
        )}
      </Spin>

      <Modal
        title={modal.editing ? 'ویرایش بخش' : 'افزودن بخش'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        destroyOnHidden
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={form} layout="vertical" className="custom-font">
          <Form.Item name="name" label="نام بخش (متن دکمه)" rules={[{ required: true, message: 'نام بخش را وارد کنید' }]}>
            <Input maxLength={100} placeholder="مثلاً: پشتیبانی فنی" />
          </Form.Item>
          <Form.Item name="description" label="توضیح (داخلی)">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="selected_message"
            label="پیام پس از انتخاب این بخش (اختیاری)"
            extra="خالی = پیام پیش‌فرض بازو. {department} با نام بخش جایگزین می‌شود."
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={1000} />
          </Form.Item>
          <Form.Item name="operator_ids" label="اپراتورهای این بخش" extra="خالی = همه اپراتورهایی که به بخشی محدود نشده‌اند.">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={operators.map((o) => ({ value: o.id, label: operatorName(o) }))}
            />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="sort_order" label="ترتیب نمایش">
              <InputNumber min={0} max={1000} className="w-full" />
            </Form.Item>
            <Form.Item name="is_active" label="فعال" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DepartmentsCard;
